import { useState } from "react";
import commentApi from "../../api/comment";
import { getAvatarText, getFileIcon, isImageFile } from "../../common";
import { CommentItem } from "../../common/type";
import CommentBox from "../CommentBox";

interface CommentProps {
    data?: CommentItem;
    onReply?: (
        parentId: number,
        payload: { comment: string; file: File | null }
    ) => void;
}

export default function Comment({ data, onReply }: CommentProps) {
    const [showReply, setShowReply] = useState(false);
    const [showCommentReply, setShowCommentReply] = useState(false);
    const [replies, setReplies] = useState<CommentItem[]>([]);
    const [loadingReplies, setLoadingReplies] = useState(false);
    const [showImageModal, setShowImageModal] = useState(false);

    if (!data) return null;

    const { id, content, user, fileUrl, fileName } = data;
    const { fullName } = user;

    const avatarText = getAvatarText(fullName);

    async function fetchAllRepliesOfComment(commentId: number) {
        setLoadingReplies(true);
        const res = await commentApi.getRepliesOfComment(commentId);
        setReplies(res.data);
        setLoadingReplies(false);
    }

    const handleShowReplies = async () => {
        if (!showCommentReply && replies.length === 0) {
            await fetchAllRepliesOfComment(id);
        }
        setShowCommentReply(prev => !prev);
    };

    const handleDownload = async (url: string, name: string) => {
        try {
            const response = await fetch(url);
            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = name;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);
        } catch (error) {
            console.error('Download failed:', error);
            window.open(url, '_blank');
        }
    };

    return (
        <>
            <div className="flex gap-2 text-left mb-4">
                <div className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center font-semibold">
                    {avatarText}
                </div>
                <div className="flex-1">
                    <div className="font-medium">{fullName}</div>
                    <div className="text-gray-700">{content}</div>
                    {fileUrl && fileName && (
                        <div className="mt-2">
                            {isImageFile(fileName) ? (
                                <div className="inline-block">
                                    <img
                                        src={fileUrl}
                                        alt={fileName}
                                        className="max-w-[240px] rounded-md border cursor-pointer hover:opacity-90 transition-opacity"
                                        onClick={() => setShowImageModal(true)}
                                    />
                                    <div className="text-xs text-gray-500 mt-1">{fileName}</div>
                                </div>
                            ) : (
                                <a
                                    href={fileUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 p-2 border rounded-md w-fit hover:bg-gray-50 transition-colors"
                                >
                                    {getFileIcon(fileName, true)}
                                    <span className="text-sm text-blue-600 underline">
                                        {fileName}
                                    </span>
                                </a>
                            )}
                        </div>
                    )}
                    <div className="flex">
                        <button
                            className="text-sm text-blue-500 mt-1 hover:underline"
                            onClick={() => setShowReply(prev => !prev)}
                        >
                            Trả lời
                        </button>
                        <button
                            className="ml-3 text-sm text-blue-500 mt-1 hover:underline"
                            onClick={handleShowReplies}
                        >
                            {showCommentReply ? "Ẩn bình luận" : "Xem tất cả bình luận"}
                        </button>
                    </div>
                    {showReply && (
                        <CommentBox
                            type="reply"
                            onGetParentCommentId={id}
                            onComment={(payload) => {
                                onReply?.(id, payload);
                                setShowReply(false);
                            }}
                        />
                    )}
                    {showCommentReply && (
                        <div className="mt-4 ml-6 border-l-2 border-gray-300 pl-4">
                            {loadingReplies ? (
                                <div className="text-sm text-gray-400">
                                    Đang tải bình luận...
                                </div>
                            ) : replies.length > 0 ? (
                                replies.map(reply => (
                                    <Comment
                                        key={reply.id}
                                        data={reply}
                                        onReply={onReply}
                                    />
                                ))
                            ) : (
                                <div className="text-sm text-gray-400">
                                    Chưa có bình luận nào
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Image Modal */}
            {showImageModal && fileUrl && fileName && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
                    onClick={() => setShowImageModal(false)}
                >
                    <div
                        className="relative max-w-5xl max-h-[90vh] w-full"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Close button */}
                        <button
                            onClick={() => setShowImageModal(false)}
                            className="absolute top-0 right-0 text-white hover:text-gray-300 transition-colors"
                            title="Đóng"
                        >
                            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>

                        {/* Image */}
                        <img
                            src={fileUrl}
                            alt={fileName}
                            className="w-full h-full object-contain rounded-lg"
                        />

                        {/* Download button */}
                        <button
                            onClick={() => handleDownload(fileUrl, fileName)}
                            className="absolute bottom-4 right-4 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 shadow-lg transition-colors"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                            Tải xuống
                        </button>

                        {/* File name */}
                        <div className="absolute bottom-4 left-4 bg-black bg-opacity-60 text-white px-3 py-2 rounded-lg text-sm">
                            {fileName}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}